import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiUsers, FiDollarSign, FiLogOut, FiShield, FiEdit2, FiTrash2, FiPlus, FiX, FiSave, FiRefreshCw, FiTool, FiKey, FiCheckCircle, FiXCircle } = FiIcons;

const AdminPanel = ({ user, onLogout, onAccessTool, showToast }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersResponse, paymentsResponse] = await Promise.all([
        supabase.from('users').select('*').order('created_at', { ascending: false }),
        supabase.from('payments').select('*').order('created_at', { ascending: false })
      ]);

      if (usersResponse.error) throw usersResponse.error;
      if (paymentsResponse.error) throw paymentsResponse.error;

      setUsers(usersResponse.data || []);
      setPayments(paymentsResponse.data || []);
    } catch (error) {
      showToast('Failed to load data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId);

      if (error) throw error;

      showToast('User updated successfully');
      loadData();
      setEditingUser(null);
    } catch (error) {
      showToast('Failed to update user: ' + error.message, 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      if (authError) throw authError;

      const { error: dbError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (dbError) throw dbError;

      showToast('User deleted successfully');
      loadData();
    } catch (error) {
      showToast('Failed to delete user: ' + error.message, 'error');
    }
  };

  const stats = {
    totalUsers: users.length,
    paidUsers: users.filter(u => u.has_paid).length,
    activeSubscriptions: users.filter(u => u.subscription_status === 'active').length,
    totalRevenue: payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    monthlyRevenue: payments.filter(p => {
      const paymentDate = new Date(p.created_at);
      const now = new Date();
      return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
    }).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    adminUsers: users.filter(u => u.is_admin).length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-red-600/20 p-2 rounded-lg">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-slate-400 text-sm">{user.email}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onAccessTool}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 rounded-lg transition-all duration-200 border border-purple-500/30"
              >
                <SafeIcon icon={FiTool} className="w-4 h-4" />
                <span>Access Tool</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={loadData}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-all duration-200"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                <span>Refresh</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-700/70 text-slate-200 rounded-lg transition-all duration-200"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span>Logout</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard icon={FiUsers} label="Total Users" value={stats.totalUsers} color="blue" />
          <StatCard icon={FiCheckCircle} label="Active Subscriptions" value={stats.activeSubscriptions} color="green" />
          <StatCard icon={FiDollarSign} label="Total Revenue" value={`$${stats.totalRevenue.toFixed(2)}`} color="purple" />
          <StatCard icon={FiDollarSign} label="Monthly Revenue" value={`$${stats.monthlyRevenue.toFixed(2)}`} color="emerald" />
          <StatCard icon={FiUsers} label="Paid Users" value={stats.paidUsers} color="teal" />
          <StatCard icon={FiShield} label="Admins" value={stats.adminUsers} color="red" />
        </div>

        {/* Tabs */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 backdrop-blur-sm overflow-hidden">
          <div className="flex border-b border-slate-700/50">
            <TabButton 
              active={activeTab === 'users'} 
              onClick={() => setActiveTab('users')}
              icon={FiUsers}
              label="Users Management"
            />
            <TabButton 
              active={activeTab === 'payments'} 
              onClick={() => setActiveTab('payments')}
              icon={FiDollarSign}
              label="Payments"
            />
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-700 border-t-blue-500"></div>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {activeTab === 'users' && (
                  <UsersTab 
                    users={users}
                    onEdit={setEditingUser}
                    onDelete={handleDeleteUser}
                    onAdd={() => setShowAddUser(true)}
                    onResetPassword={setResetPasswordUser}
                  />
                )}
                {activeTab === 'payments' && (
                  <PaymentsTab payments={payments} users={users} />
                )}
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editingUser && (
          <EditUserModal
            user={editingUser}
            onClose={() => setEditingUser(null)}
            onSave={handleUpdateUser}
          />
        )}
      </AnimatePresence>

      {/* Add User Modal */}
      <AnimatePresence>
        {showAddUser && (
          <AddUserModal
            onClose={() => setShowAddUser(false)}
            onSuccess={() => {
              setShowAddUser(false);
              loadData();
            }}
            showToast={showToast}
          />
        )}
      </AnimatePresence>

      {/* Reset Password Modal */}
      <AnimatePresence>
        {resetPasswordUser && (
          <ResetPasswordModal
            user={resetPasswordUser}
            onClose={() => setResetPasswordUser(null)}
            showToast={showToast}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => {
  const colors = {
    blue: 'from-blue-600/20 to-blue-700/20 text-blue-400 border-blue-500/30',
    green: 'from-green-600/20 to-green-700/20 text-green-400 border-green-500/30',
    purple: 'from-purple-600/20 to-purple-700/20 text-purple-400 border-purple-500/30',
    red: 'from-red-600/20 to-red-700/20 text-red-400 border-red-500/30',
    emerald: 'from-emerald-600/20 to-emerald-700/20 text-emerald-400 border-emerald-500/30',
    teal: 'from-teal-600/20 to-teal-700/20 text-teal-400 border-teal-500/30'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${colors[color]} rounded-xl p-6 border backdrop-blur-sm`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm mb-1">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
        <SafeIcon icon={icon} className="w-8 h-8 opacity-50" />
      </div>
    </motion.div>
  );
};

const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all duration-200 ${
      active
        ? 'bg-slate-700/50 text-white border-b-2 border-blue-500'
        : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
    }`}
  >
    <SafeIcon icon={icon} className="w-5 h-5" />
    <span>{label}</span>
  </button>
);

const UsersTab = ({ users, onEdit, onDelete, onAdd, onResetPassword }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-xl font-semibold text-white">All Users</h3>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onAdd}
        className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200"
      >
        <SafeIcon icon={FiPlus} className="w-4 h-4" />
        <span>Add User</span>
      </motion.button>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-3 px-4 text-slate-400 font-medium">Name</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium">Subscription</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium">Role</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium">Joined</th>
            <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors">
              <td className="py-3 px-4 text-white">{user.first_name} {user.last_name}</td>
              <td className="py-3 px-4 text-slate-300">{user.email}</td>
              <td className="py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  user.has_paid ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                }`}>
                  {user.has_paid ? 'Paid' : 'Unpaid'}
                </span>
              </td>
              <td className="py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  user.subscription_status === 'active' 
                    ? 'bg-green-600/20 text-green-400' 
                    : user.subscription_status === 'cancelled'
                    ? 'bg-yellow-600/20 text-yellow-400'
                    : 'bg-slate-600/20 text-slate-400'
                }`}>
                  {user.subscription_status || 'None'}
                </span>
              </td>
              <td className="py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  user.is_admin ? 'bg-red-600/20 text-red-400' : 'bg-blue-600/20 text-blue-400'
                }`}>
                  {user.is_admin ? 'Admin' : 'User'}
                </span>
              </td>
              <td className="py-3 px-4 text-slate-400 text-sm">
                {new Date(user.created_at).toLocaleDateString()}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center justify-end space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onResetPassword(user)}
                    className="p-2 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded-lg transition-colors"
                    title="Send Password Reset Email"
                  >
                    <SafeIcon icon={FiKey} className="w-4 h-4" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onEdit(user)}
                    className="p-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onDelete(user.id)}
                    className="p-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </motion.button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </motion.div>
);

const PaymentsTab = ({ payments, users }) => {
  // Create a map of user IDs to user data for quick lookup
  const userMap = users.reduce((map, user) => {
    map[user.id] = user;
    return map;
  }, {});

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <h3 className="text-xl font-semibold text-white mb-6">Payment History</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-3 px-4 text-slate-400 font-medium">User</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Subscription ID</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Amount</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Next Billing</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(payment => {
              const user = payment.user_id ? userMap[payment.user_id] : null;
              return (
                <tr key={payment.id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors">
                  <td className="py-3 px-4 text-white">
                    {payment.user_name || (user ? `${user.first_name} ${user.last_name}` : 'Pending')}
                  </td>
                  <td className="py-3 px-4 text-slate-300">
                    {payment.user_email || (user?.email) || 'N/A'}
                  </td>
                  <td className="py-3 px-4 text-slate-400 font-mono text-sm">
                    {payment.subscription_id?.substring(0, 16)}...
                  </td>
                  <td className="py-3 px-4 text-green-400 font-semibold">
                    ${parseFloat(payment.amount).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded-full text-xs font-medium">
                      {payment.payment_type || 'subscription'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      payment.status === 'active' || payment.status === 'completed'
                        ? 'bg-green-600/20 text-green-400'
                        : payment.status === 'cancelled'
                        ? 'bg-red-600/20 text-red-400'
                        : 'bg-yellow-600/20 text-yellow-400'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-sm">
                    {payment.next_billing_date 
                      ? new Date(payment.next_billing_date).toLocaleDateString()
                      : 'N/A'
                    }
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-sm">
                    {new Date(payment.created_at).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

const EditUserModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    has_paid: user.has_paid,
    subscription_status: user.subscription_status || 'active',
    is_admin: user.is_admin,
    admin_notes: user.admin_notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user.id, formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-800 rounded-2xl border border-slate-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Edit User</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2 text-sm">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2 text-sm">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-300 mb-2 text-sm">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-2 text-sm">Subscription Status</label>
            <select
              value={formData.subscription_status}
              onChange={(e) => setFormData({...formData, subscription_status: e.target.value})}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.has_paid}
                onChange={(e) => setFormData({...formData, has_paid: e.target.checked})}
                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-slate-300 text-sm">Has Paid</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_admin}
                onChange={(e) => setFormData({...formData, is_admin: e.target.checked})}
                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-red-600 focus:ring-2 focus:ring-red-500"
              />
              <span className="text-slate-300 text-sm">Admin</span>
            </label>
          </div>

          <div>
            <label className="block text-slate-300 mb-2 text-sm">Admin Notes</label>
            <textarea
              value={formData.admin_notes}
              onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white h-24 resize-none"
              placeholder="Internal notes about this user..."
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="flex-1 flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>Save Changes</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={onClose}
              className="px-6 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition-colors"
            >
              Cancel
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const AddUserModal = ({ onClose, onSuccess, showToast }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    has_paid: true,
    is_admin: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.first_name,
            last_name: formData.last_name
          }
        }
      });

      if (authError) throw authError;

      const { error: dbError } = await supabase
        .from('users')
        .insert([{
          id: authData.user.id,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          has_paid: formData.has_paid,
          is_admin: formData.is_admin
        }]);

      if (dbError) throw dbError;

      showToast('User created successfully');
      onSuccess();
    } catch (error) {
      showToast('Failed to create user: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-800 rounded-2xl border border-slate-700 p-6 max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Add New User</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2 text-sm">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2 text-sm">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-300 mb-2 text-sm">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-2 text-sm">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white"
              required
              minLength={6}
            />
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.has_paid}
                onChange={(e) => setFormData({...formData, has_paid: e.target.checked})}
                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-slate-300 text-sm">Has Paid</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_admin}
                onChange={(e) => setFormData({...formData, is_admin: e.target.checked})}
                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-red-600 focus:ring-2 focus:ring-red-500"
              />
              <span className="text-slate-300 text-sm">Admin</span>
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>{loading ? 'Creating...' : 'Create User'}</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={onClose}
              className="px-6 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition-colors"
            >
              Cancel
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const ResetPasswordModal = ({ user, onClose, showToast }) => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendResetEmail = async () => {
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}`
      });

      if (error) throw error;

      setSent(true);
      showToast(`Password reset email sent to ${user.email}`);
    } catch (error) {
      showToast('Failed to send reset email: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-800 rounded-2xl border border-slate-700 p-6 max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">Send Password Reset</h3>
            <p className="text-slate-400 text-sm mt-1">
              {user.first_name} {user.last_name}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {sent ? (
          <div className="text-center py-6">
            <div className="inline-block bg-green-600/20 p-4 rounded-full mb-4">
              <SafeIcon icon={FiKey} className="w-12 h-12 text-green-400" />
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Email Sent!</h4>
            <p className="text-slate-400 mb-2">
              Password reset email has been sent to:
            </p>
            <p className="text-blue-400 font-medium mb-6">{user.email}</p>
            <p className="text-slate-500 text-sm">
              The user can click the link in their email to reset their password.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg transition-colors"
            >
              Close
            </motion.button>
          </div>
        ) : (
          <div>
            <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiKey} className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-blue-400 font-medium mb-1">Send Reset Email</p>
                  <p className="text-slate-300 text-sm">
                    A password reset link will be sent to:
                  </p>
                  <p className="text-white font-medium mt-1">{user.email}</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-3 mb-6">
              <p className="text-yellow-400 text-sm">
                ℹ️ The user will receive an email with a secure link to reset their password. The link expires after 1 hour.
              </p>
            </div>

            <div className="flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSendResetEmail}
                disabled={loading}
                className="flex-1 flex items-center justify-center space-x-2 bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiKey} className="w-4 h-4" />
                    <span>Send Reset Email</span>
                  </>
                )}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={onClose}
                className="px-6 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg transition-colors"
              >
                Cancel
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AdminPanel;